import {
  MAX_BATCH_BYTES,
  MAX_BATCH_OPS,
  type Clock,
  type HlcClock,
  type OpEnvelope,
} from '@foerier/shared'

import type { LoggedOp, OpLog } from './opLog'
import type { PullBody, PushOutcome, Transport } from './transport'

/**
 * The client half of `/sync` (`docs/sync-protocol.md` §6): it drains the
 * outbox, pages the pull, keeps the cursor, backs off, and dead-letters what
 * the household will never accept. Everything it needs is injected — the log,
 * the transport, both clocks, the randomness behind the jitter and the timer
 * behind the backoff — so every tier below Tier 4 drives it by hand.
 *
 * ## Two orderings, both from §8.5, both silent data loss when reversed
 *
 * - **Author:** append to the local log *before* updating in-memory state. A
 *   crash between the two loses a render, not a fact. The engine owns only
 *   the log half of that; `store.ts` owns the other.
 * - **Pull:** fold and durably write the page *before* advancing the cursor.
 *   The cursor is the only record of what has been seen, so advancing it
 *   first turns any failure in between into permanent loss. Hence the exact
 *   order in {@link createSyncEngine}'s pull loop — `ingest`, then `onOps`,
 *   then `writeMeta('cursor')` — and hence an `onOps` that throws propagates
 *   with the cursor untouched rather than being swallowed.
 *
 *   `ingest` is the durable half of that pair and `onOps` only the memory
 *   half, which is why `store.ts` is free to return from `onOps` as soon as
 *   it has queued the fold: a fold that fails there no longer holds the
 *   cursor back, and nothing is lost by it, because the page is already in
 *   the log and the next load folds it from there (§8.4).
 *
 * ## `has_more` is the sole paging condition (§6.4)
 *
 * `household_seq` is a snapshot taken *after* the page was read, so
 * `has_more: false` alongside `cursor < household_seq` is legal and ordinary
 * on a household being written to. It is a **denominator**, and a moving one:
 * never a loop condition. A client that paged until `cursor` caught
 * `household_seq` would never finish against a busy household — and the
 * screen that would hang is the first sync, the app's one unavoidable loading
 * screen. The remainder simply arrives on the next ordinary pull.
 *
 * ## A dead-lettered op stays in the log and stays folded (§6.5)
 *
 * Dead-lettering is a publication failure, not a retraction. The op leaves
 * the outbox and nothing else; dropping it from the fold would make the
 * device's own state jump backwards under the user's hands.
 *
 * ## Nothing here runs on a render path
 *
 * `start()` wires the triggers — `online`, `visibilitychange`, and a
 * 30-second interval — and `store.ts` nudges `flush()` after every `emit`.
 */

/** §6.3: base 1 s, doubling. */
export const BACKOFF_BASE_MS = 1_000

/** §6.3: capped at 5 minutes, retrying indefinitely. */
export const BACKOFF_CAP_MS = 5 * 60 * 1_000

/** The heartbeat that picks up other devices' work while the app is open. */
export const SYNC_INTERVAL_MS = 30_000

/** §6.4's own default. The server's maximum is 1000. */
export const DEFAULT_PAGE_SIZE = 500

export type SyncStatus =
  'idle' | 'syncing' | 'offline' | 'signed-out' | 'bootstrapping'

/**
 * `total` is `household_seq` — the household's op count, because `seq` is
 * gapless (§6.4) — which is what makes the first sync determinate rather than
 * a spinner (§7.6). It may grow mid-flight, and completion is `has_more`,
 * never `folded === total`.
 *
 * **`total: 0` means not known yet**, not "nothing to fold": `household_seq`
 * only arrives with the first page, so a bootstrap reports `0 / 0` for
 * exactly one round trip and the screen renders that denominator as `—`.
 */
export interface BootstrapProgress {
  folded: number
  total: number
  paused: boolean
}

export interface SyncEngine {
  flush(): Promise<void>
  /**
   * `options.force` clears an armed backoff window before running — for an
   * explicit user gesture only (`store.retrySync()`), never for the ordinary
   * 30-second interval or the `online`/`visibilitychange` triggers, which all
   * go through `trigger()` → `run('sync')` and never see this flag. Plain
   * `pull()` still respects the window like everything else.
   */
  pull(options?: { force?: boolean }): Promise<void>
  start(): void
  stop(): void
  status(): SyncStatus
  bootstrap(): BootstrapProgress | null
}

/**
 * Schedules one backoff retry and returns a cancel function. Injected for the
 * same reason `Clock` is: a test drives the retry by hand instead of waiting
 * out five real minutes, and the delay it was asked for is observable.
 */
export type Schedule = (fn: () => Promise<void>, ms: number) => () => void

export interface SyncEngineDeps {
  log: OpLog
  transport: Transport
  clock: Clock
  hlc: HlcClock
  /** Received ops, for the store to fold. Called before the cursor moves. */
  onOps(ops: readonly OpEnvelope[]): void
  onStatus(s: SyncStatus): void
  onBootstrap(p: BootstrapProgress | null): void
  /** The jitter's source. Injected so a test can assert bounds exactly. */
  random?(): number
  schedule?: Schedule
  pageSize?: number
}

const defaultSchedule: Schedule = (fn, ms) => {
  const id = setTimeout(() => {
    void fn()
  }, ms)
  return () => clearTimeout(id)
}

const BATCH_ENVELOPE_BYTES = '{"ops":[]}'.length
const encoder = new TextEncoder()

/**
 * An op's size on the wire, in bytes — what §1.4's two caps are measured in.
 * Shared with `store.ts`, which applies the per-op cap at authoring time,
 * because a length in characters is not a length in bytes.
 */
export function opByteLength(op: OpEnvelope): number {
  return encoder.encode(JSON.stringify(op)).length
}

/**
 * Takes the longest prefix of `records` that fits §6.1's 1 MB body limit,
 * counting the JSON the body will actually carry — the array's brackets and
 * the commas between ops included. Always takes at least one record: a single
 * op that cannot fit is a poison pill the caller dead-letters rather than a
 * batch to shrink further.
 */
function chunkOf(records: readonly LoggedOp[]): LoggedOp[] {
  const chunk: LoggedOp[] = []
  let bytes = BATCH_ENVELOPE_BYTES
  for (const record of records) {
    const size = opByteLength(record.op) + 1
    if (chunk.length > 0 && bytes + size > MAX_BATCH_BYTES) break
    bytes += size
    chunk.push(record)
  }
  return chunk
}

export function createSyncEngine(deps: SyncEngineDeps): SyncEngine {
  const random = deps.random ?? Math.random
  const schedule = deps.schedule ?? defaultSchedule
  const pageSize = deps.pageSize ?? DEFAULT_PAGE_SIZE

  let current: SyncStatus = 'idle'
  let progress: BootstrapProgress | null = null

  /** Set by a 401 and never cleared: re-authenticating builds a new engine. */
  let frozen = false
  /** A pull is owed — a push saw a higher mark, or a pull did not finish. */
  let pullWanted = false

  let attempt = 0
  let retryAt = 0
  let cancelRetry: (() => void) | null = null
  let queue: Promise<void> = Promise.resolve()

  let interval: ReturnType<typeof setInterval> | null = null
  let onOnline: (() => void) | null = null
  let onVisibility: (() => void) | null = null

  function setStatus(next: SyncStatus): void {
    if (next === current) return
    current = next
    deps.onStatus(next)
  }

  /** Silent when nothing about the fold changed: `BootstrapProgress` is a
   * value, and re-reporting the same three numbers only costs the first-sync
   * screen a render. */
  function setProgress(next: BootstrapProgress | null): void {
    if (
      next !== null &&
      progress !== null &&
      next.folded === progress.folded &&
      next.total === progress.total &&
      next.paused === progress.paused
    ) {
      return
    }
    progress = next
    deps.onBootstrap(next)
  }

  async function readCursor(): Promise<number> {
    return (await deps.log.readMeta<number>('cursor')) ?? 0
  }

  // -------------------------------------------------------------------------
  // Backoff — §6.3
  // -------------------------------------------------------------------------

  /** Full jitter: anywhere in `[0, window]`, so a household's devices cannot
   * re-form a thundering herd on the way back up. */
  function backoffDelay(): number {
    const window = Math.min(BACKOFF_CAP_MS, BACKOFF_BASE_MS * 2 ** attempt)
    return Math.floor(random() * window)
  }

  function clearBackoff(): void {
    attempt = 0
    retryAt = 0
    cancelRetry?.()
    cancelRetry = null
  }

  function retryLater(failure: { retryAfter?: number }): void {
    // A 429's Retry-After is the server telling us when; anything else gets
    // the jittered backoff. Either way the attempt counts, so a repeated 429
    // still backs off (§6.3).
    const delay =
      failure.retryAfter === undefined
        ? backoffDelay()
        : Math.max(0, failure.retryAfter) * 1_000
    attempt += 1
    retryAt = deps.clock.now() + delay
    cancelRetry?.()
    cancelRetry = schedule(() => {
      cancelRetry = null
      retryAt = 0
      return serial(() => run('sync'))
    }, delay)
    setStatus('offline')
  }

  function freeze(): void {
    frozen = true
    cancelRetry?.()
    cancelRetry = null
    retryAt = 0
    // A frozen engine never runs again — recovery is a *new* engine, built
    // once the app has a token — so it lets go of the interval and both DOM
    // listeners here rather than leaving them for a `stop()` the caller has
    // no obvious reason to make. `start()`'s guard tolerates the unwiring.
    unwire()
    // A bootstrap caught by a 401 is paused like any other interruption: its
    // cursor is kept, and it resumes once the app has a token again.
    pauseBootstrap()
    // The outbox is untouched. A 401 must never cost a user queued offline
    // work (§6.3).
    setStatus('signed-out')
  }

  /** Releases the interval and both listeners. Idempotent; shared by
   * {@link freeze} and `stop()`. */
  function unwire(): void {
    if (interval !== null) {
      clearInterval(interval)
      interval = null
    }
    if (onOnline !== null) {
      window.removeEventListener('online', onOnline)
      onOnline = null
    }
    if (onVisibility !== null) {
      document.removeEventListener('visibilitychange', onVisibility)
      onVisibility = null
    }
  }

  // -------------------------------------------------------------------------
  // Push — the outbox
  // -------------------------------------------------------------------------

  function deadLetterAll(
    chunk: readonly LoggedOp[],
    code: string,
  ): Promise<void> {
    return deps.log.deadLetter(
      chunk.map((record) => ({ opId: record.op.id, code })),
    )
  }

  /** Returns how many records the response resolved, which is what tells the
   * loop it is making progress. */
  async function applyOutcomes(
    results: readonly PushOutcome[],
  ): Promise<number> {
    const pushed: { opId: string; seq: number }[] = []
    const rejected: { opId: string; code: string }[] = []

    for (const outcome of results) {
      if (outcome.status === 'rejected') {
        rejected.push({ opId: outcome.op_id, code: outcome.code ?? 'rejected' })
      } else if (typeof outcome.seq === 'number') {
        // `accepted` and `duplicate` are the same event to a client: the op
        // is in the household's log at this seq (§8.1).
        pushed.push({ opId: outcome.op_id, seq: outcome.seq })
      }
    }

    if (pushed.length > 0) await deps.log.markPushed(pushed)
    if (rejected.length > 0) await deps.log.deadLetter(rejected)
    return pushed.length + rejected.length
  }

  /** Returns `true` when it halted — frozen or backing off — and the caller
   * must not carry on to the pull. */
  async function pushOutbox(): Promise<boolean> {
    const cursor = await readCursor()
    let limit = MAX_BATCH_OPS

    for (;;) {
      const records = await deps.log.outbox(limit)
      if (records.length === 0) return false

      const chunk = chunkOf(records)
      const result = await deps.transport.push(chunk.map((record) => record.op))

      if (!result.ok) {
        if (result.status === 401) {
          freeze()
          return true
        }
        if (result.status === 400) {
          // The batch itself is malformed — a client bug. Dead-letter it, do
          // not retry (§6.3); it stays in the log and stays folded (§6.5).
          await deadLetterAll(chunk, result.code)
          continue
        }
        if (result.status === 413) {
          if (chunk.length === 1) {
            // Unhalvable. Retrying forever would wedge every op behind it.
            await deadLetterAll(chunk, result.code)
            continue
          }
          limit = Math.floor(chunk.length / 2)
          continue
        }
        // A bootstrap interrupted here is paused exactly as `pullPages`
        // pauses one: otherwise a push failure mid-fold leaves the card
        // frozen with no `RETRY NOW` and no explanation.
        pauseBootstrap()
        retryLater(result)
        return true
      }

      clearBackoff()
      const settled = await applyOutcomes(result.body.results)
      if (result.body.household_seq > cursor) pullWanted = true

      if (settled === 0) {
        // The server answered but resolved nothing, so the outbox cannot
        // shrink and looping would spin. Leave it for the next flush.
        console.warn('sync: a push resolved no ops; leaving them queued')
        return false
      }
    }
  }

  // -------------------------------------------------------------------------
  // Pull — the cursor
  // -------------------------------------------------------------------------

  function pauseBootstrap(): void {
    if (progress !== null && !progress.paused) {
      // The cursor is kept, so this resumes; it never restarts (§7.6).
      setProgress({ ...progress, paused: true })
    }
  }

  function reportBootstrap(fresh: boolean, page: PullBody): void {
    if (!fresh || progress === null) return
    const folded = progress.folded + page.ops.length
    // The denominator moves, and may already be behind what we just folded.
    setProgress({
      folded,
      total: Math.max(page.household_seq, folded),
      paused: false,
    })
  }

  /** Returns `true` when it halted. */
  async function pullPages(): Promise<boolean> {
    const seen = await deps.log.readMeta<number>('cursor')
    let cursor = seen ?? 0
    // A device that has never *written* a cursor has never pulled, so this is
    // its first sync — as is one already part-way through a bootstrap that
    // paused. That is not `cursor === 0`: both the real server and the fake
    // return `cursor: ops.at(-1)?.seq ?? since`, so a household with no ops
    // ever leaves an empty device's cursor at `0`, and testing `=== 0` would
    // re-enter the bootstrap on every 30-second trigger. `writeMeta('cursor',
    // page.cursor)` below runs on every page, including a page whose cursor is
    // `0`, so `readMeta` returning `null` is the one test that actually means
    // "never pulled" — `readCursor()`'s `?? 0` answers a different question,
    // the loop's starting value, and must not be reused for this one.
    //
    // The **cursor**, never the log's emptiness: the cursor means "has never
    // pulled" and an empty log means "has never authored", and a Device that
    // joined, recorded gear offline and only then first-synced holds a
    // non-empty log at cursor 0 while folding the household's whole history.
    const fresh = progress !== null || seen === null

    // `household_seq` arrives *with* the first page (§6.4), so the
    // denominator is unknown for exactly one round trip. Report the fold
    // anyway, with `total: 0` for "not known yet": the alternative is a first
    // sync that shows nothing at all until a page lands, and a CTA that
    // enables and then disables under the user's finger (§7.6).
    if (fresh && progress === null) {
      setProgress({ folded: 0, total: 0, paused: false })
      setStatus('bootstrapping')
    }

    for (;;) {
      const result = await deps.transport.pull(cursor, pageSize)

      if (!result.ok) {
        if (result.status === 401) {
          freeze()
          return true
        }
        if (result.status === 400) {
          // A malformed request is a client bug; there is no batch to
          // dead-letter, so record it and stop rather than spin.
          console.error('sync: /sync/pull rejected the request', result.code)
          pauseBootstrap()
          setStatus('idle')
          return true
        }
        pauseBootstrap()
        retryLater(result)
        return true
      }

      clearBackoff()
      const page = result.body

      // §2.5, once per op received, whether or not this device authored it.
      for (const op of page.ops) deps.hlc.receive(op.hlc)

      await deps.log.ingest(page.ops)
      deps.onOps(page.ops)
      // Only now. Everything above is durable or in memory; the cursor is the
      // only record of what has been seen (§8.5).
      await deps.log.writeMeta('cursor', page.cursor)
      cursor = page.cursor

      reportBootstrap(fresh, page)

      // `has_more` alone. Never `cursor < household_seq` (§6.4).
      if (!page.has_more) break
    }

    pullWanted = false
    if (progress !== null) setProgress(null)
    return false
  }

  // -------------------------------------------------------------------------
  // The one serialised worker
  // -------------------------------------------------------------------------

  function serial(work: () => Promise<void>): Promise<void> {
    const next = queue.then(work)
    // The tail must never reject, or one failed run — a fold that threw, say
    // — would skip every sync queued behind it. The caller's own promise
    // still carries the failure.
    queue = next.catch(() => undefined)
    return next
  }

  function canRun(): boolean {
    if (frozen) {
      // The interval and both DOM listeners are unwired the moment this
      // engine freezes (`freeze()`), so the only way to land here is an
      // explicit caller — `retrySync()`, most likely — reaching a frozen
      // engine after the fact. Otherwise that call is a silent no-op with
      // nothing to point at (§6.3: re-authenticating builds a new engine).
      console.warn('sync: refused — this engine is frozen; sign in again')
      return false
    }
    // Respect a backoff window rather than letting a trigger stampede past
    // it. The scheduled retry clears `retryAt` before it runs.
    return deps.clock.now() >= retryAt
  }

  /**
   * `flush` pushes and pulls only if the push earned one; `sync` (the
   * triggers, and every backoff retry) always pulls, because that is how a
   * device learns what the others have done; `pull` skips the push.
   */
  async function run(mode: 'flush' | 'sync' | 'pull'): Promise<void> {
    if (!canRun()) return
    if (mode !== 'flush') pullWanted = true
    setStatus(progress === null ? 'syncing' : 'bootstrapping')

    if (mode !== 'pull' && (await pushOutbox())) return
    if (pullWanted && (await pullPages())) return

    attempt = 0
    setStatus('idle')
  }

  function trigger(): void {
    void serial(() => run('sync')).catch((error: unknown) => {
      console.error('sync: a triggered sync failed', error)
    })
  }

  return {
    flush: () => serial(() => run('flush')),
    pull: (options) => {
      if (options?.force) {
        // An explicit user gesture — `RETRY NOW` — is at least as strong a
        // signal as the `online` handler above, which clears the same
        // backoff on the weaker evidence that the network is merely back.
        // Cleared before `canRun()` runs, so the very tap that asked for
        // this pull is the one that gets to make it through the window
        // rather than being refused by the window it is trying to end.
        clearBackoff()
      }
      return serial(() => run('pull'))
    },

    start() {
      if (interval !== null) return
      onOnline = () => {
        // The network is visibly back, so the reason for the backoff is gone.
        clearBackoff()
        trigger()
      }
      onVisibility = () => {
        if (document.visibilityState === 'visible') trigger()
      }
      window.addEventListener('online', onOnline)
      document.addEventListener('visibilitychange', onVisibility)
      interval = setInterval(trigger, SYNC_INTERVAL_MS)
    },

    stop() {
      unwire()
      cancelRetry?.()
      cancelRetry = null
    },

    status: () => current,
    bootstrap: () => progress,
  }
}
